import { createFileRoute, notFound, redirect } from "@tanstack/react-router"
import { SITE_URL } from "@/lib/constants/site-url"
import { BAKERY_PHONE, SITE_NAME } from "@/lib/constants/site"
import {
  isCateringVisible,
  siteSettingsQueryOptions,
} from "@/lib/data/site-settings"
import CateringPage from "@/pages/catering"
import { isCateringCatalogProduct } from "@/lib/utils/catalog-visibility"
import { jsonLd } from "@/lib/utils/json-ld"
import { sanitize } from "@/lib/utils/sanitize"
import { CATERING_FAQ } from "@/lib/data/catering-faq"
import {
  CATERING_BOXES,
  CATERING_BUNDLES,
  CATERING_DRINKS,
  CATERING_ONLINE_LEAD_TIME_HOURS,
  CATERING_PAGE_ASSETS,
  CATERING_SERVICES,
  getMinQuantity,
  type CateringMenuItem,
} from "@/lib/data/catering-menu"

const CATERING_PRODUCT_FIELDS =
  "id,title,handle,thumbnail,metadata,*categories,*variants,+variants.inventory_quantity,+variants.manage_inventory,+variants.allow_backorder,+variants.calculated_price"

/** One schema.org Offer per cartable item. */
const toOffer = (item: CateringMenuItem) => {
  if (item.pricing.type === "quote") return null
  const minQuantity = getMinQuantity(item)

  return {
    "@type": "Offer",
    name: item.name,
    description: item.tagline,
    priceCurrency: "USD",
    price: item.pricing.amount.toFixed(2),
    availability: "https://schema.org/InStock",
    ...(minQuantity > 1
      ? {
          eligibleQuantity: {
            "@type": "QuantitativeValue",
            minValue: minQuantity,
            unitText:
              item.pricing.type === "per_person" ? "person" : item.pricing.unitLabel,
          },
        }
      : {}),
  }
}

export const Route = createFileRoute("/$countryCode/catering")({
  loader: async ({ params, context }) => {
    const { countryCode } = params
    const { queryClient } = context

    // Catering visibility is a runtime toggle staff control in Admin →
    // Settings → Store. `VITE_CATERING_PREVIEW=true` opens the page on Vercel
    // previews so the redesign can be reviewed before the toggle is flipped.
    const siteSettings = await queryClient.ensureQueryData(
      siteSettingsQueryOptions
    )
    // While the page is hidden, send visitors to the contact form (it has a
    // "Catering Inquiry" option) rather than a 404. Old WordPress URLs like
    // /catering-services 301 here, so a 404 would drop every one of those
    // visitors. Temporary on purpose: once the toggle is on, this URL serves
    // the page again and nothing cached a permanent redirect away from it.
    if (!isCateringVisible(siteSettings)) {
      throw redirect({
        to: "/$countryCode/contact",
        params: { countryCode },
        statusCode: 307,
      })
    }
    const [
      { getRegion },
      { listProducts },
      { retrieveCategoryTreeIds, CATERING_ROOT_CATEGORY_HANDLE },
    ] = await Promise.all([
      import("@/lib/data/regions"),
      import("@/lib/data/products"),
      import("@/lib/data/categories"),
    ])

    const region = await queryClient.ensureQueryData({
      queryKey: ["region", countryCode],
      queryFn: () => getRegion({ country_code: countryCode }),
    })

    if (!region) {
      throw notFound()
    }

    let cateringCategoryIds: string[] = []
    try {
      cateringCategoryIds = await queryClient.ensureQueryData({
        queryKey: ["category-tree-ids", CATERING_ROOT_CATEGORY_HANDLE],
        queryFn: () =>
          retrieveCategoryTreeIds({ handle: CATERING_ROOT_CATEGORY_HANDLE }),
      })
    } catch (error) {
      console.warn("[+Bake Cafe] Failed to resolve catering category tree", error)
    }

    const productsData = await queryClient.ensureQueryData({
      queryKey: ["catering-products", region.id, cateringCategoryIds],
      queryFn: async () => {
        const { products } = await listProducts({
          region_id: region.id,
          query_params: {
            ...(cateringCategoryIds.length > 0
              ? { category_id: cateringCategoryIds }
              : {}),
            limit: 100,
            fields: CATERING_PRODUCT_FIELDS,
          },
        })

        return products.filter(isCateringCatalogProduct)
      },
    })

    return sanitize({ countryCode, region, products: productsData })
  },
  head: ({ loaderData }) => {
    // The loader redirects when catering is hidden (and throws notFound() when
    // the region is missing), so no loader data means the page is not
    // rendering and has no tags to contribute.
    if (!loaderData) {
      return {}
    }
    const title = "Catering in Rancho Cucamonga | +Bake Cafe"
    const description =
      "Croissant trays, cookies by the dozen, rice bowls and coffee for offices and parties from +Bake Cafe. Serving Rancho Cucamonga and the Inland Empire."
    const canonical = `${SITE_URL}/catering`
    // The OG file ships with the photo shoot; fall back to the site image
    // rather than advertising a URL that 404s, which is what the old
    // route-local constant ended up doing.
    const ogImage = CATERING_PAGE_ASSETS.pending
      ? `${SITE_URL}/images/plusbake/optimized/og-home-1200x630.jpg`
      : `${SITE_URL}${CATERING_PAGE_ASSETS.ogImage}`

    const offers = [
      ...CATERING_BUNDLES,
      ...CATERING_BOXES,
      ...CATERING_DRINKS,
    ]
      .map(toOffer)
      .filter(Boolean)

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Catering",
      name: `${SITE_NAME} Catering`,
      description,
      provider: {
        "@type": "Bakery",
        name: SITE_NAME,
        url: SITE_URL,
        telephone: BAKERY_PHONE,
      },
      areaServed: [
        { "@type": "City", name: "Rancho Cucamonga" },
        { "@type": "City", name: "Upland" },
        { "@type": "City", name: "Ontario" },
        { "@type": "City", name: "Fontana" },
        { "@type": "City", name: "Claremont" },
        { "@type": "City", name: "Chino Hills" },
        { "@type": "City", name: "Pomona" },
        { "@type": "AdministrativeArea", name: "Inland Empire" },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: `${SITE_NAME} Catering Menu`,
        itemListElement: offers,
      },
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        description: `Catering orders are placed a minimum of ${CATERING_ONLINE_LEAD_TIME_HOURS} hours in advance. On-site services such as the Latte Station and Dessert Table need at least 7 days.`,
      },
      url: canonical,
    }

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: CATERING_FAQ.map((entry) => ({
        "@type": "Question",
        name: entry.q,
        acceptedAnswer: { "@type": "Answer", text: entry.a },
      })),
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Catering", item: canonical },
      ],
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: ogImage },
        { property: "og:site_name", content: SITE_NAME },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
        // Services are the schema-visible half of the menu; the quote-only
        // ones are named here so an AI answer engine can still list them.
        {
          name: "keywords",
          content: [
            "catering Rancho Cucamonga",
            "office breakfast catering Inland Empire",
            "boxed lunch catering Ontario CA",
            ...CATERING_SERVICES.map((service) => service.name),
          ].join(", "),
        },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: jsonLd(serviceSchema) },
        { type: "application/ld+json", children: jsonLd(faqSchema) },
        { type: "application/ld+json", children: jsonLd(breadcrumbSchema) },
      ],
    }
  },
  component: CateringPage,
})
